import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';
import { toast } from '../hooks/use-toast';
import { Eye, EyeOff, ArrowRight, Loader2 } from 'lucide-react';
import { motion } from 'framer-motion';
import awloLogo from '../assets/AWLO1.png'; 
import authImg from '../assets/authImg.png';

// --- VARIANTES D'ANIMATION ---
const kenBurnsEffect = {
  initial: { scale: 1, x: '0%', y: '0%' },
  animate: { 
    scale: 1.1, 
    x: '-2%', 
    y: '-1%',
    transition: { 
      duration: 20,
      ease: "easeInOut", 
      repeat: Infinity,
      repeatType: "reverse"
    }
  }
};

const logoFadeIn = {
  initial: { opacity: 0 },
  animate: { 
    opacity: 1, 
    transition: { delay: 0.5, duration: 1 }
  }
};

const LoginPage = () => {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const [formData, setFormData] = useState({ email: '', password: '' });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    const result = await login(formData.email, formData.password);
    if (result.success) {
      const userName = result.user.firstName || result.user.first_name || 'utilisateur';
      const userRole = (result.user.role || '').toLowerCase();
      toast({ title: 'Connexion réussie', description: `Bienvenue ${userName} !`, duration: 3000 });
      setTimeout(() => {
        if (userRole === 'admin') navigate('/admin');
        else if (userRole === 'professional') navigate('/professional');
        else if (userRole === 'client') navigate('/client');
        else navigate('/');
      }, 500);
    } else {
      toast({ title: 'Erreur de connexion', description: result.error, variant: 'destructive', duration: 4000 });
    }
    setLoading(false);
  };

  return (
    <div className="relative min-h-screen w-full flex items-center justify-center p-4 sm:p-8 overflow-hidden font-sans">
      
      {/* --- BACKGROUND --- */}
      <div className="absolute inset-0 z-0 overflow-hidden">
        <motion.div 
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: `url(${authImg})` }}
          variants={kenBurnsEffect}
          initial="initial"
          animate="animate"
        />
        <div className="absolute inset-0 bg-black/60 backdrop-blur-[2px]" />
      </div>

      {/* --- LOGO FLOTTANT AU-DESSUS --- */}
      <motion.div 
        variants={logoFadeIn}
        initial="initial"
        animate="animate"
        className="absolute top-[30%] left-[20%] md:left-[25%] lg:left-[32%] z-50 hidden md:block pointer-events-auto"
      >
        <Link
          to="/"
          className="block hover:opacity-90 transition-opacity focus:outline-none cursor-pointer"
          aria-label="Retour à l'accueil"
        >
          <img
            src={awloLogo}
            alt="AWLO"
            className="h-24 lg:h-32 w-auto object-contain drop-shadow-2xl brightness-110"
          />
        </Link>
      </motion.div>

      {/* --- GRID LAYOUT --- */}
      <div className="relative z-10 w-full max-w-6xl grid lg:grid-cols-2 gap-12 items-center">
        
        {/* --- PARTIE GAUCHE : SLOGAN --- */}
        <motion.div 
          initial={{ opacity: 0, x: -30 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="hidden lg:flex flex-col justify-center items-start space-y-6 pl-10"
        >
          <div className="text-white space-y-2 mt-32">
            <h2 className="text-4xl font-bold leading-tight">Votre histoire<br/>commence ici.</h2>
            <p className="text-white/70 text-lg max-w-md font-light">
              Connectez-vous pour accéder à des milliers de biens et gérer vos projets immobiliers.
            </p>
          </div>
        </motion.div>

        {/* --- PARTIE DROITE : FORMULAIRE TRANSPARENT --- */}
        <div className="flex justify-center lg:justify-end pr-0 lg:pr-10">
          <motion.div 
            initial={{ opacity: 0, scale: 0.98 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.4, ease: "easeOut", delay: 0.1 }}
            className="w-full max-w-[420px] bg-black/20 backdrop-blur-md rounded-3xl border border-white/10 p-8 shadow-2xl"
          >
            
            {/* Logo pour Mobile uniquement */}
            <div className="lg:hidden flex justify-center mb-8">
              <Link
                to="/"
                className="block hover:opacity-90 transition-opacity focus:outline-none cursor-pointer pointer-events-auto"
                aria-label="Retour à l'accueil"
              >
                <img src={awloLogo} alt="AWLO" className="h-16 w-auto object-contain drop-shadow-lg" />
              </Link>
            </div>

            <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }} className="mb-8">
              <h1 className="text-3xl font-bold text-white mb-2 tracking-tight">Bon retour !</h1>
            </motion.div>

            <form onSubmit={handleSubmit} className="space-y-5">
              
              {/* Email */}
              <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }} className="space-y-1.5">
                <Label htmlFor="email" className="text-xs font-semibold text-white/80 uppercase tracking-wider ml-1">Email</Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="nom@exemple.com"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className="h-12 bg-white/10 border-white/10 rounded-xl text-white placeholder:text-white/40 focus:ring-2 focus:ring-[#FF9F68] focus:border-transparent focus:bg-white/20 transition-all"
                  required
                />
              </motion.div>

              {/* Mot de passe */}
              <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.4 }} className="space-y-1.5">
                <div className="flex justify-between items-center ml-1">
                  <Label htmlFor="password" className="text-xs font-semibold text-white/80 uppercase tracking-wider">Mot de passe</Label>
                  <Link to="/forgot-password" className="text-xs font-medium text-[#FF9F68] hover:text-[#FFB085] transition-colors">
                    Oublié ?
                  </Link>
                </div>
                <div className="relative">
                  <Input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    placeholder="••••••••"
                    value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    className="h-12 bg-white/10 border-white/10 rounded-xl text-white pr-10 focus:ring-2 focus:ring-[#FF9F68] focus:border-transparent focus:bg-white/20 transition-all"
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-white/50 hover:text-white transition-colors focus:outline-none"
                  >
                    {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                  </button>
                </div>
              </motion.div>

              {/* Checkbox */}
              <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.5 }} className="flex items-center space-x-3 ml-1 py-1">
                <Checkbox
                  id="remember"
                  checked={rememberMe}
                  onCheckedChange={setRememberMe}
                  className="border-white/30 data-[state=checked]:bg-[#FF9F68] data-[state=checked]:border-[#FF9F68] data-[state=checked]:text-white rounded shadow-sm"
                />
                <Label htmlFor="remember" className="text-sm text-white/70 font-medium cursor-pointer select-none hover:text-white transition-colors">
                  Rester connecté
                </Label>
              </motion.div>

              {/* Bouton Submit */}
              <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.6 }} className="pt-2">
                <Button
                  type="submit"
                  disabled={loading}
                  className="w-full h-12 bg-[#FF9F68] hover:bg-[#E67E4F] text-white font-bold rounded-xl shadow-lg shadow-orange-900/20 transition-all duration-300 transform hover:-translate-y-0.5 disabled:opacity-70 disabled:cursor-not-allowed"
                >
                  {loading ? (
                    <span className="flex items-center gap-2">
                      <Loader2 className="h-5 w-5 animate-spin" />
                      Connexion...
                    </span>
                  ) : (
                    <span className="flex items-center gap-2">
                      Se connecter <ArrowRight className="h-5 w-5" />
                    </span>
                  )}
                </Button>
              </motion.div>
            </form>

            {/* Footer Card */}
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.7 }} className="mt-8 pt-6 border-t border-white/10 text-center">
              <p className="text-sm text-white/60">
                Nouveau sur AWLO ?{' '}
                <Link to="/register" className="font-bold text-white hover:text-[#FF9F68] transition-colors inline-flex items-center gap-1 group">
                  Créer un compte
                  <ArrowRight className="h-3 w-3 transition-transform group-hover:translate-x-1" />
                </Link>
              </p>
            </motion.div>

          </motion.div>
        </div>
      </div>
      
      {/* Copyright discret */}
      <div className="absolute bottom-4 left-0 w-full text-center z-10 pointer-events-none">
         <p className="text-white/20 text-xs font-medium">© 2025 AWLO</p>
      </div>

    </div>
  );
};

export default LoginPage;